import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowRightLeft,
  MoreHorizontal,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Stethoscope,
} from 'lucide-react';
import { useState } from 'react';

import {
  getCatalogoDiagnosticosControllerListarQueryKey,
  useCatalogoDiagnosticosControllerAtualizar,
  useCatalogoDiagnosticosControllerCategorias,
  useCatalogoDiagnosticosControllerDesativar,
  useCatalogoDiagnosticosControllerListar,
} from '@/api/generated/admin-catalogo-diagnosticos/admin-catalogo-diagnosticos';
import type { CatalogoDiagnosticoResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { DiagnosticoFormDialogo } from '@/features/admin/catalogo-diagnosticos/components/DiagnosticoFormDialogo';
import { FundirDiagnosticoDialogo } from '@/features/admin/catalogo-diagnosticos/components/FundirDiagnosticoDialogo';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const TODOS = 'todos';

/**
 * Catálogo de diagnósticos — administração (B14-1).
 *
 * ⚠️ Catálogo GLOBAL, e nada é apagado: `PacienteDiagnostico` aponta para cá, e
 * o diagnóstico continua sendo o que aquele animal recebeu. "Excluir" é
 * desativar; duplicata se resolve com fusão.
 *
 * Mais simples que o de fármacos por uma razão só: diagnóstico não tem
 * apresentação comercial.
 */
export function CatalogoDiagnosticosPage() {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [categoria, setCategoria] = useState<string>(TODOS);
  const [situacao, setSituacao] = useState<string>(TODOS);
  const [formAberto, setFormAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<CatalogoDiagnosticoResponse | undefined>();
  const [aFundir, setAFundir] = useState<CatalogoDiagnosticoResponse | null>(null);
  const [aDesativar, setADesativar] = useState<CatalogoDiagnosticoResponse | null>(null);

  // A busca do servidor atravessa nome e sinônimos, sem acento ("HAC" acha
  // "Hiperadrenocorticismo") e roda em memória lá — uma requisição por tecla
  // seria varredura do catálogo inteiro a cada letra.
  const buscaAdiada = useDebounce(busca);

  const { data, isPending, isError, refetch } = useCatalogoDiagnosticosControllerListar({
    pagina,
    porPagina: 20,
    ordenarPor: 'nome',
    ordem: 'asc',
    ...(buscaAdiada.trim() ? { busca: buscaAdiada.trim() } : {}),
    ...(categoria !== TODOS ? { categoria } : {}),
    ...(situacao !== TODOS ? { ativo: situacao as 'true' | 'false' } : {}),
  });

  const { data: categorias } = useCatalogoDiagnosticosControllerCategorias();
  const desativar = useCatalogoDiagnosticosControllerDesativar();
  const atualizar = useCatalogoDiagnosticosControllerAtualizar();

  const podeCriar = pode('admin_catalogo_diagnosticos.criar');
  const podeEditar = pode('admin_catalogo_diagnosticos.editar');
  const podeExcluir = pode('admin_catalogo_diagnosticos.excluir');

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  async function recarregar() {
    await queryClient.invalidateQueries({
      queryKey: getCatalogoDiagnosticosControllerListarQueryKey(),
    });
  }

  async function confirmarDesativacao() {
    if (!aDesativar) return;
    try {
      await desativar.mutateAsync({ id: aDesativar.id });
      await recarregar();
      toast.success(`"${aDesativar.nome}" desativado.`);
    } catch (erro) {
      toast.error(
        erro instanceof ApiError ? erro.message : 'Não foi possível desativar o diagnóstico.',
      );
    } finally {
      setADesativar(null);
    }
  }

  async function reativar(diagnostico: CatalogoDiagnosticoResponse) {
    try {
      await atualizar.mutateAsync({ id: diagnostico.id, data: { ativo: true } });
      await recarregar();
      toast.success(`"${diagnostico.nome}" reativado.`);
    } catch (erro) {
      toast.error(
        erro instanceof ApiError ? erro.message : 'Não foi possível reativar o diagnóstico.',
      );
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <Input
            value={busca}
            onChange={(e) => aoFiltrar(() => setBusca(e.target.value))}
            placeholder="Buscar por nome ou sigla..."
            aria-label="Buscar diagnóstico"
            className="sm:max-w-xs"
          />

          {categorias && categorias.itens.length > 0 && (
            <Select value={categoria} onValueChange={(v) => aoFiltrar(() => setCategoria(v))}>
              <SelectTrigger className="sm:w-56" aria-label="Filtrar por categoria">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todas as categorias</SelectItem>
                {categorias.itens.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={situacao} onValueChange={(v) => aoFiltrar(() => setSituacao(v))}>
            <SelectTrigger className="sm:w-40" aria-label="Filtrar por status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos os status</SelectItem>
              <SelectItem value="true">Ativos</SelectItem>
              <SelectItem value="false">Desativados</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {podeCriar && (
          <Button
            onClick={() => {
              setEmEdicao(undefined);
              setFormAberto(true);
            }}
          >
            <Plus className="w-4 h-4" />
            Novo diagnóstico
          </Button>
        )}
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">
            Catálogo de diagnósticos
          </h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={Stethoscope}
            titulo="Nenhum diagnóstico encontrado"
            descricao={
              busca
                ? 'Tente outro termo — a busca também olha os sinônimos.'
                : 'Cadastre o primeiro diagnóstico padronizado.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Diagnóstico</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Em uso</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-20">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((diagnostico) => (
                    <TableRow key={diagnostico.id}>
                      <TableCell>
                        <p className="font-medium text-foreground">{diagnostico.nome}</p>

                        {diagnostico.sinonimos.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {diagnostico.sinonimos.join(' · ')}
                          </p>
                        )}

                        {/* Item fundido: sem a seta, a linha desativada parece
                            simplesmente abandonada, e ninguém descobre para
                            onde a leitura foi. */}
                        {diagnostico.substituidoPorNome && (
                          <p className="text-xs text-amber-600 mt-0.5">
                            → fundido em {diagnostico.substituidoPorNome}
                          </p>
                        )}
                      </TableCell>

                      <TableCell className="text-muted-foreground">
                        {diagnostico.categoria ?? '—'}
                      </TableCell>

                      {/* ⚠️ `totalEmUso` não é enfeite: é em quantos pacientes
                          este diagnóstico está registrado. É o número que
                          explica por que aqui se desativa em vez de apagar. */}
                      <TableCell className="text-muted-foreground tabular-nums">
                        <span
                          title={`${diagnostico.totalEmUso} paciente(s) com este diagnóstico`}
                        >
                          {diagnostico.totalEmUso}
                        </span>
                      </TableCell>

                      <TableCell>
                        {diagnostico.ativo ? (
                          <Badge
                            variant="outline"
                            className="text-emerald-600 border-emerald-600/40"
                          >
                            Ativo
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-destructive border-destructive/40"
                          >
                            Desativado
                          </Badge>
                        )}
                      </TableCell>

                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Ações de ${diagnostico.nome}`}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {podeEditar && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setEmEdicao(diagnostico);
                                    setFormAberto(true);
                                  }}
                                >
                                  <Pencil className="w-4 h-4" />
                                  Editar
                                </DropdownMenuItem>

                                {/* Item já fundido não funde de novo: o servidor
                                    recusa, e oferecer a ação seria oferecer um
                                    409. */}
                                {diagnostico.ativo && !diagnostico.substituidoPorId && (
                                  <DropdownMenuItem onClick={() => setAFundir(diagnostico)}>
                                    <ArrowRightLeft className="w-4 h-4" />
                                    Fundir em outro
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}

                            {podeExcluir &&
                              (diagnostico.ativo ? (
                                <DropdownMenuItem
                                  onClick={() => setADesativar(diagnostico)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <PowerOff className="w-4 h-4" />
                                  Desativar
                                </DropdownMenuItem>
                              ) : (
                                podeEditar && (
                                  <DropdownMenuItem onClick={() => void reativar(diagnostico)}>
                                    <Power className="w-4 h-4" />
                                    Reativar
                                  </DropdownMenuItem>
                                )
                              ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>

      <DiagnosticoFormDialogo
        aberto={formAberto}
        aoFechar={() => setFormAberto(false)}
        {...(emEdicao ? { diagnostico: emEdicao } : {})}
      />

      {aFundir && (
        <FundirDiagnosticoDialogo
          aberto
          aoFechar={() => setAFundir(null)}
          diagnostico={aFundir}
        />
      )}

      {/* ⚠️ O aviso cita o número de pacientes de propósito: é ele que separa
          "desativar" de "apagar". Nada some do histórico. */}
      <AlertDialog open={Boolean(aDesativar)} onOpenChange={(v) => !v && setADesativar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar &quot;{aDesativar?.nome}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              {aDesativar?.totalEmUso === 0 ? (
                <>
                  Nenhum paciente tem este diagnóstico hoje. Ele sai dos seletores e pode ser
                  reativado a qualquer momento.
                </>
              ) : (
                <>
                  <strong>
                    {aDesativar?.totalEmUso} pacientes têm este diagnóstico e continuarão
                    mostrando-o.
                  </strong>{' '}
                  Ele apenas sai dos seletores para novos registros. Se a intenção é resolver uma
                  duplicata, use “Fundir em outro” — desativar sem fundir deixa essa coorte fora da
                  Análise Comparativa.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmarDesativacao()}>
              Desativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
